import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, Users, MapPin, LogOut, Menu, X, ChevronRight, BarChart3, CalendarOff, Gift, Clock, Settings, Shield, FileBarChart, Building2, UserCheck, Filter } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { adminAPI } from '@/lib/api';
import { BranchFilterProvider, useBranchFilter } from './BranchFilterContext';

const AuditIcon = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={props.size || 18} height={props.size || 18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M14 2v4a2 2 0 0 0 2 2h4"/><path d="M4.268 21a2 2 0 0 0 1.727 1H18a2 2 0 0 0 2-2V7l-5-5H6a2 2 0 0 0-2 2v3"/><path d="m9 18-1.5-1.5"/><circle cx="5" cy="14" r="3"/>
  </svg>
);

const BriefcaseIcon = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={props.size || 18} height={props.size || 18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M16 20V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/><rect width="20" height="14" x="2" y="6" rx="2"/>
  </svg>
);

const sidebarItems = [
  { path: '/admin', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/admin/daily-summary', icon: FileBarChart, label: 'Daily Summary' },
  { path: '/admin/employees', icon: Users, label: 'Employees' },
  { path: '/admin/designations', icon: BriefcaseIcon, label: 'Designations' },
  { path: '/admin/rms', icon: UserCheck, label: 'Reporting Managers' },
  { path: '/admin/branches', icon: Building2, label: 'Branches' },
  { path: '/admin/zones', icon: MapPin, label: 'Geo Zones' },
  { path: '/admin/leaves', icon: CalendarOff, label: 'Leaves' },
  { path: '/admin/late-sanctions', icon: Clock, label: 'Late Sanctions' },
  { path: '/admin/work-permissions', icon: Shield, label: 'Work Permits' },
  { path: '/admin/holidays', icon: Gift, label: 'Holidays' },
  { path: '/admin/analytics', icon: BarChart3, label: 'Analytics' },
  { path: '/admin/audit-trail', icon: AuditIcon, label: 'Audit Trail' },
  { path: '/admin/settings', icon: Settings, label: 'Settings' },
];

export default function AdminLayout({ children }) {
  return (
    <BranchFilterProvider>
      <AdminLayoutInner>{children}</AdminLayoutInner>
    </BranchFilterProvider>
  );
}

function AdminLayoutInner({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { branch, setBranch, branches } = useBranchFilter();

  const handleLogout = async () => {
    try { await adminAPI.logout(); } catch {}
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  const handleNav = (path) => {
    navigate(path);
    setSidebarOpen(false);
  };

  return (
    <div className="flex min-h-screen min-h-[100dvh] bg-[#f4f4f5]">
      {/* Mobile Header */}
      <header className="fixed top-0 left-0 right-0 z-50 md:hidden bg-[#1a2529] text-white h-14 flex items-center px-4 justify-between">
        <button
          data-testid="sidebar-toggle"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="touch-target flex items-center justify-center"
        >
          {sidebarOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
        <span className="font-bold text-sm tracking-tight">UPASTITH Admin</span>
        <button data-testid="admin-logout-mobile" onClick={handleLogout} className="touch-target flex items-center justify-center">
          <LogOut size={20} />
        </button>
      </header>

      {/* Sidebar overlay (mobile) */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 bg-black/50 md:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed top-0 left-0 z-50 h-full w-64 bg-[#1a2529] text-white flex flex-col transition-transform duration-300 ease-out md:translate-x-0 md:static md:z-auto',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="p-5 border-b border-white/10">
          <h1 className="font-extrabold text-lg tracking-tight">UPASTITH</h1>
          <p className="text-xs text-white/50 mt-0.5">Uplift Health Hubs</p>
        </div>

        {branches.length > 0 && (
          <div className="px-4 pt-3 pb-1">
            <div className="flex items-center gap-2 mb-1.5">
              <Filter size={12} className="text-white/40" />
              <span className="text-[10px] font-semibold uppercase tracking-wider text-white/40">Branch Filter</span>
            </div>
            <select
              data-testid="branch-filter-select"
              value={branch}
              onChange={(e) => setBranch(e.target.value)}
              className="w-full bg-white/10 text-white text-xs rounded-md px-2.5 py-2 border border-white/10 focus:outline-none focus:border-[#026536] appearance-none cursor-pointer"
            >
              <option value="" className="bg-[#1a2529]">All Branches</option>
              {branches.map(b => (
                <option key={b.id} value={b.id} className="bg-[#1a2529]">{b.name}</option>
              ))}
            </select>
            {branch && (
              <button onClick={() => setBranch('')} className="text-[10px] text-[#4ade80] mt-1 hover:underline">
                Clear filter
              </button>
            )}
          </div>
        )}

        <nav className="flex-1 p-3 space-y-1">
          {sidebarItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.path}
                data-testid={`admin-nav-${item.label.toLowerCase().replace(' ', '-')}`}
                onClick={() => handleNav(item.path)}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all active:scale-[0.98]',
                  isActive
                    ? 'bg-[#026536] text-white shadow-lg shadow-[#026536]/20'
                    : 'text-white/60 hover:text-white hover:bg-white/5'
                )}
              >
                <item.icon size={18} strokeWidth={1.5} />
                {item.label}
                {isActive && <ChevronRight size={14} className="ml-auto" />}
              </button>
            );
          })}
        </nav>

        <div className="p-3 border-t border-white/10">
          <Button
            data-testid="admin-logout"
            variant="ghost"
            onClick={handleLogout}
            className="w-full justify-start gap-3 text-white/60 hover:text-white hover:bg-white/5"
          >
            <LogOut size={18} />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 md:ml-0 mt-14 md:mt-0 overflow-y-auto">
        <div className="p-4 md:p-8 max-w-7xl mx-auto">
          {children}
        </div>
      </main>
    </div>
  );
}
